import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import { put, takeLatest, all, take, call, select } from 'redux-saga/effects'
import { updateUserSignature } from '@tabeeb/modules/account/actions'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { ContentFormRecordType } from '@tabeeb/enums'
import { getIsContentReadonly } from '@tabeeb/modules/readonlyWhiteboard/selectors'
import {
  getReadonlyContentFormRequest,
  getReadonlyFormAnswersByTabRequest,
  ReadonlyWhiteboardKeys,
} from '@tabeeb/modules/readonlyWhiteboard/requests'
import { queryClient } from '@tabeeb/services/queryClient'
import { getReadonlyContentFormSuccess, getReadonlyFormAnswerByTabSuccess } from '@tabeeb/shared/content/actions'
import { SagaIterator } from 'redux-saga'

import * as rawActions from '../actions'
import { getCurrentForm } from '../selectors'
import { startLoadingFormAnswers } from '../actions'

function* setCurrentTabs(action: ReturnType<typeof rawActions.setCurrentTabs>): SagaIterator {
  const { payload } = action
  const form: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const selectedTabs = (payload || form.Tabspanels?.map((panel) => panel.Tabs![0].Id)) ?? []

  const isContentReadonly: ReturnType<typeof getIsContentReadonly> = yield select(getIsContentReadonly)
  if (isContentReadonly) {
    yield put(rawActions.setReadonlySelectedTabs(selectedTabs))

    const pathname = yield select((state) => state.router.location.pathname)
    const tokenMatch = pathname.match(/^\/content\/shared\/([^/]+)/)
    const token = tokenMatch ? tokenMatch[1] : null
    if (token != null && selectedTabs.length > 0 && form.Id) {
      try {
        // TODO: Remove query client usage from saga
        const data = yield call((options) => queryClient.fetchQuery(options), {
          queryKey: [
            ReadonlyWhiteboardKeys.readonlyContent,
            { Token: token, ContentFormId: form.Id, TabIds: selectedTabs },
          ],
          queryFn: getReadonlyFormAnswersByTabRequest,
        })

        yield put(getReadonlyFormAnswerByTabSuccess({ LatestFormAnswers: data }))
      } catch {
        yield put(onAddErrorNotification({ message: 'Form not found' }))
      }
    }
  } else {
    yield put(rawActions.setSelectedTabs(selectedTabs))
  }
}

function* openForm(action: ReturnType<typeof rawActions.openForm>): SagaIterator {
  const contentFormId = action.payload
  yield put(rawActions.getFormControls.request({ contentFormId }))
}

function* onOpenReadonlyForm(action: ReturnType<typeof rawActions.openReadonlyForm>): SagaIterator {
  const contentFormId = action.payload

  const pathname = yield select((state) => state.router.location.pathname)
  const tokenMatch = pathname.match(/^\/content\/shared\/([^/]+)/)
  const token = tokenMatch ? tokenMatch[1] : null

  yield put(startLoadingFormAnswers())
  try {
    // TODO: Remove query client usage from saga
    const data = yield call((options) => queryClient.fetchQuery(options), {
      queryKey: [ReadonlyWhiteboardKeys.readonlyContent, { Token: token, ContentFormId: contentFormId }],
      queryFn: getReadonlyContentFormRequest,
    })

    yield put(getReadonlyContentFormSuccess(data))
  } catch {
    yield put(onAddErrorNotification({ message: 'Form not found' }))
  }
}

function* createUserSignature({ payload }: ReturnType<typeof rawActions.createUserSignature>): SagaIterator {
  const form: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const currentUserId: ReturnType<typeof getCurrentUserId> = yield select(getCurrentUserId)

  yield put(updateUserSignature.request(payload))
  yield take(updateUserSignature.success)

  yield put(
    rawActions.saveFormRecord.request({
      contentFormId: form.Id!,
      userId: currentUserId,
      type: ContentFormRecordType.UserSignature,
    })
  )
}

function* saveFormRecordSuccess() {
  yield put(onAddSuccessNotification({ message: 'The form record created successfully' }))
}

function* updateUserSignatureSuccess() {
  yield put(onAddSuccessNotification({ message: 'The signature updated successfully' }))
}

function* currentForm() {
  yield all([
    takeLatest(rawActions.openForm, openForm),
    takeLatest(rawActions.openReadonlyForm, onOpenReadonlyForm),
    takeLatest(rawActions.setCurrentTabs, setCurrentTabs),
    takeLatest(rawActions.createUserSignature, createUserSignature),
    takeLatest(rawActions.saveFormRecord.success, saveFormRecordSuccess),
    takeLatest(updateUserSignature.success, updateUserSignatureSuccess),
  ])
}

export default currentForm
